import { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { adminList, adminUpsertSingleton } from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, Field, Card } from './_ui';

export default function ResultsSection() {
  const [state, setState] = useState({
    eyebrow: 'Results',
    heading: '',
    intro: '',
    comparisons: [],
    gradeJump: { value: 2, label: 'grades', suffix: '' },
    footnote: '',
  });
  const [loaded, setLoaded] = useState(false);
  const saver = useSaver();

  useEffect(() => {
    (async () => {
      const doc = await adminList('results');
      if (doc && Object.keys(doc).length) {
        setState((prev) => ({
          ...prev,
          eyebrow: doc.eyebrow ?? prev.eyebrow,
          heading: doc.heading ?? '',
          intro: doc.intro ?? '',
          comparisons: doc.comparisons ?? [],
          gradeJump: doc.gradeJump ?? prev.gradeJump,
          footnote: doc.footnote ?? '',
        }));
      }
      setLoaded(true);
    })();
  }, []);

  const save = () => saver.run(async () => {
    await adminUpsertSingleton('results', {
      eyebrow: state.eyebrow,
      heading: state.heading,
      intro: state.intro,
      comparisons: state.comparisons.map((c) => ({
        id: c.id, label: c.label, metric: c.metric,
        us: Number(c.us), national: Number(c.national),
      })),
      gradeJump: {
        value: Number(state.gradeJump.value),
        label: state.gradeJump.label,
        suffix: state.gradeJump.suffix,
      },
      footnote: state.footnote,
    });
  });

  const setCmp = (i, patch) =>
    setState((s) => ({
      ...s,
      comparisons: s.comparisons.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));

  if (!loaded) return <p className="text-brand-ink/60">Loading...</p>;

  return (
    <div className="max-w-4xl">
      <SectionHeader
        title="Results"
        description="Public statistics shown in the animated Results section on the homepage."
      />
      <Card className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow">
            <Input
              value={state.eyebrow}
              onChange={(e) => setState({ ...state, eyebrow: e.target.value })}
            />
          </Field>
          <Field label="Heading">
            <Input
              value={state.heading}
              onChange={(e) => setState({ ...state, heading: e.target.value })}
            />
          </Field>
          <Field label="Intro / cohort" hint='e.g. "Based on our 2025/26 cohort."' cols={2}>
            <Input
              value={state.intro}
              onChange={(e) => setState({ ...state, intro: e.target.value })}
            />
          </Field>
        </div>

        <div>
          <div className="text-sm font-semibold text-brand-ink mt-2 mb-2">
            National comparisons
          </div>
          <div className="space-y-2">
            {state.comparisons.map((c, i) => (
              <div key={c.id || i} className="grid md:grid-cols-4 gap-2 items-end">
                <Field label="Label">
                  <Input value={c.label || ''} onChange={(e) => setCmp(i, { label: e.target.value })} />
                </Field>
                <Field label="Metric" hint='e.g. "A* / A"'>
                  <Input value={c.metric || ''} onChange={(e) => setCmp(i, { metric: e.target.value })} />
                </Field>
                <Field label="Us (%)">
                  <Input type="number" step="0.1" value={c.us ?? ''} onChange={(e) => setCmp(i, { us: e.target.value })} />
                </Field>
                <Field label="National (%)">
                  <Input type="number" step="0.1" value={c.national ?? ''} onChange={(e) => setCmp(i, { national: e.target.value })} />
                </Field>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Avg grade jump - value">
            <Input
              type="number"
              value={state.gradeJump.value}
              onChange={(e) => setState({ ...state, gradeJump: { ...state.gradeJump, value: e.target.value } })}
            />
          </Field>
          <Field label="Avg grade jump - unit label" hint='e.g. "grades"'>
            <Input
              value={state.gradeJump.label}
              onChange={(e) => setState({ ...state, gradeJump: { ...state.gradeJump, label: e.target.value } })}
            />
          </Field>
          <Field label="Suffix sentence">
            <Input
              value={state.gradeJump.suffix || ''}
              onChange={(e) => setState({ ...state, gradeJump: { ...state.gradeJump, suffix: e.target.value } })}
            />
          </Field>
          <Field label="Footnote / source note" cols={3}>
            <Textarea
              rows={2}
              value={state.footnote}
              onChange={(e) => setState({ ...state, footnote: e.target.value })}
            />
          </Field>
        </div>

        <div className="pt-2">
          <SaveBar {...saver} onSave={save} dataTestId="save-results" />
        </div>
      </Card>
    </div>
  );
}
